<template>
  <div class="space-y-4">
    <p class="font-rounded font-bold uppercase text-purple-950">
      {{ $t("ticketPurchase.tickets.chooseYourTicket") }}
    </p>
    <div
      v-if="ticketTypes.length"
      class="max-h-[52dvh] space-y-3 overflow-y-auto pr-1 md:max-h-[46vh]"
    >
      <div
        v-for="ticket in ticketTypes"
        :key="ticketId(ticket)"
        class="rounded-xl border p-4 hover:border-pink-500"
        :class="{ 'opacity-50': isSoldOut(ticket) }"
      >
        <div
          class="flex flex-col gap-3 md:flex-row md:items-center md:justify-between"
        >
          <div class="min-w-0 flex-1">
            <div class="flex items-start justify-between gap-3">
              <div>
                <div class="font-semibold text-[#2e234b]">
                  {{ ticket.name ?? ticket.ticketName ?? "Ticket" }}
                </div>
                <div class="text-sm text-gray-600">
                  {{
                    ticket.description ??
                    $t("ticketPurchase.tickets.generalAdmission")
                  }}
                </div>
              </div>
              <div class="whitespace-nowrap font-bold text-[#2e234b]">
                {{
                  (ticket.price ?? 0) === 0
                    ? $t("ticketPurchase.tickets.free")
                    : `${ticket.price}${ticket.currency ?? currency}`
                }}
              </div>
            </div>

            <span
              v-if="isSoldOut(ticket)"
              class="mt-2 inline-block rounded-full bg-red-50 px-2 py-0.5 text-xs font-semibold text-red-600"
            >
              {{ $t("ticketPurchase.tickets.soldOut") }}
            </span>
            <span
              v-else-if="remaining(ticket) !== null && remaining(ticket)! <= 10"
              class="mt-2 inline-block rounded-full bg-amber-50 px-2 py-0.5 text-xs font-semibold text-amber-700"
            >
              {{
                $t("ticketPurchase.tickets.left", { count: remaining(ticket) })
              }}
            </span>
          </div>

          <div class="flex items-center justify-end gap-3 md:pl-4">
            <button
              class="h-10 w-10 rounded-full border text-[#2e234b] disabled:opacity-40"
              :aria-label="`Decrease ${ticket.name ?? ticket.ticketName ?? 'ticket'} count`"
              :disabled="(ticketCounts[ticketId(ticket)] ?? 0) === 0"
              @click="decrement(ticket)"
            >
              -
            </button>
            <span
              class="min-w-[28px] text-center font-semibold text-[#2e234b]"
              >{{ ticketCounts[ticketId(ticket)] ?? 0 }}</span
            >
            <button
              class="h-10 w-10 rounded-full border bg-pink-500 text-white disabled:opacity-40"
              :aria-label="`Increase ${ticket.name ?? ticket.ticketName ?? 'ticket'} count`"
              :disabled="
                isSoldOut(ticket) ||
                (remaining(ticket) !== null &&
                  (ticketCounts[ticketId(ticket)] ?? 0) >= remaining(ticket)!)
              "
              @click="increment(ticket)"
            >
              +
            </button>
          </div>
        </div>
      </div>
    </div>
    <div v-else class="text-sm text-gray-500">
      {{ $t("ticketPurchase.tickets.noTicketsAvailable") }}
    </div>
  </div>
</template>

<script setup lang="ts">
import { availableQuantity, isUnavailable } from "./availability";

type TicketTypeLike = {
  id?: string | number;
  ticketTypeId?: string | number;
  name?: string;
  ticketName?: string;
  description?: string;
  price?: number;
  currency?: string;
  maxQuantity?: number;
  sold?: number;
  held?: number;
  available?: number | null;
};

const props = defineProps<{
  ticketTypes: TicketTypeLike[];
  ticketCounts: Record<string, number>;
  currency: string;
}>();

const emit = defineEmits<{
  (e: "update:ticketCounts", value: Record<string, number>): void;
}>();

const ticketId = (t: TicketTypeLike) =>
  String(t.id ?? t.ticketTypeId ?? t.name ?? "ticket");

function isSoldOut(ticket: TicketTypeLike): boolean {
  return isUnavailable(ticket);
}

function remaining(ticket: TicketTypeLike): number | null {
  return availableQuantity(ticket);
}

function increment(ticket: TicketTypeLike) {
  if (isSoldOut(ticket)) return;
  const id = ticketId(ticket);
  const current = props.ticketCounts[id] ?? 0;
  const rem = remaining(ticket);
  if (rem !== null && current >= rem) return;
  emit("update:ticketCounts", {
    ...props.ticketCounts,
    [id]: current + 1,
  });
}

function decrement(ticket: TicketTypeLike) {
  const id = ticketId(ticket);
  emit("update:ticketCounts", {
    ...props.ticketCounts,
    [id]: Math.max((props.ticketCounts[id] ?? 0) - 1, 0),
  });
}
</script>
